import React, { useCallback } from 'react';
import Global from '../../../../../../../store/global-selectors';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuItemGroup } from 'ui-components/components/dropdown-menu-item-group';
import shippingOrderBarcodes from '../barcode-types/shipping-orders';
import printingDocumentBarcodes from '../barcode-types/printing-documents';
import generalBarcodes from '../barcode-types/general';
import navigationBarcodes from '../barcode-types/navigation';
import { convertPresetToBarcodeProperties } from '../preset-to-barcode-conversion-service';
import {
  BARCODE_SCAN_ACTION_TYPE_GENERAL,
  BARCODE_SCAN_ACTION_TYPE_SHIPPING_PRESETS,
  BARCODE_SCAN_ACTION_TYPE_SHIPPING_ORDERS,
  BARCODE_SCAN_ACTION_TYPE_PRINTING_DOCUMENTS,
  BARCODE_SCAN_ACTION_TYPE_NAVIGATION,
} from '../../../report-filters/constants';
import { FormattedMessage, useIntl } from 'react-intl';

import messages from './messages';
import style from '../../../report-filters/style.module.less';

const scanActionToBarcodeListMap = {
  [BARCODE_SCAN_ACTION_TYPE_GENERAL]: generalBarcodes,
  [BARCODE_SCAN_ACTION_TYPE_SHIPPING_ORDERS]: shippingOrderBarcodes,
  [BARCODE_SCAN_ACTION_TYPE_PRINTING_DOCUMENTS]: printingDocumentBarcodes,
  [BARCODE_SCAN_ACTION_TYPE_NAVIGATION]: navigationBarcodes,
};
const scanActionToLabelMap = {
  [BARCODE_SCAN_ACTION_TYPE_GENERAL]: <FormattedMessage {...messages.scanTypeGeneral} />,
  [BARCODE_SCAN_ACTION_TYPE_SHIPPING_PRESETS]: <FormattedMessage {...messages.scanTypePresets} />,
  [BARCODE_SCAN_ACTION_TYPE_SHIPPING_ORDERS]: <FormattedMessage {...messages.scanTypeShippingOrders} />,
  [BARCODE_SCAN_ACTION_TYPE_PRINTING_DOCUMENTS]: <FormattedMessage {...messages.scanTypePrintingDocuments} />,
  [BARCODE_SCAN_ACTION_TYPE_NAVIGATION]: <FormattedMessage {...messages.scanTypeNavigation} />,
};

const barcodeDropdownGroups = [
  BARCODE_SCAN_ACTION_TYPE_GENERAL,
  BARCODE_SCAN_ACTION_TYPE_SHIPPING_PRESETS,
  BARCODE_SCAN_ACTION_TYPE_SHIPPING_ORDERS,
  BARCODE_SCAN_ACTION_TYPE_PRINTING_DOCUMENTS,
  BARCODE_SCAN_ACTION_TYPE_NAVIGATION,
];

const BarcodeSelector = ({ ordinalPosition, onBarcodeSelected, disabled, disabledTooltipMessage, selectedBarcodeName }) => {
  const intl = useIntl();
  const userPresets = useSelector(Global.selectSellerPresets);
  const label = `#${ordinalPosition + 1}`;
  const _onBarcodeSelected = useCallback(
    (barcode) => {
      const { barcodeContent, intlFormattedName, hotkey, name } = barcode;
      const selectedName = intlFormattedName ? intl.formatMessage(intlFormattedName) : name;
      onBarcodeSelected({
        ordinalPosition,
        barcode: {
          barcodeContent,
          name: selectedName,
          hotkey: hotkey.toString(),
        },
      });
    },
    [intl, onBarcodeSelected, ordinalPosition],
  );

  return (
    <div className={style['filter-input']}>
      <label>{label}</label>

      <DropdownMenu className={style['dropdown-menu']} disabled={disabled} disabledTooltipMessage={disabledTooltipMessage}>
        <DropdownMenuToggler className={style['dropdown-toggler']}>
          {selectedBarcodeName ?? <FormattedMessage {...messages.selectBarcode} />}
        </DropdownMenuToggler>
        {barcodeDropdownGroups.map((action) => {
          const barcodes =
            action === BARCODE_SCAN_ACTION_TYPE_SHIPPING_PRESETS
              ? convertPresetToBarcodeProperties(userPresets)
              : scanActionToBarcodeListMap[action];
          return (
            <DropdownMenuItemGroup key={`${ordinalPosition}-${action}`} label={scanActionToLabelMap[action]}>
              {barcodes.map((barcode, itemIndex) => {
                const { intlFormattedName, name } = barcode;
                return (
                  <DropdownMenuItem key={itemIndex} onClick={() => _onBarcodeSelected(barcode)} theme='unstyled'>
                    {intlFormattedName ? <FormattedMessage {...intlFormattedName} /> : name}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuItemGroup>
          );
        })}
      </DropdownMenu>
    </div>
  );
};

BarcodeSelector.propTypes = {
  ordinalPosition: PropTypes.number.isRequired,
  onBarcodeSelected: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  selectedBarcodeName: PropTypes.string,
};

export default BarcodeSelector;
